import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useForm, type UseFormReturn } from 'react-hook-form';
import moment, { type Moment } from 'moment';
import api from '~/services/api';
import { LojaId, validateSearch } from '../utils/validateSearch';
import { toast } from 'react-toastify';
import { EmissaoPayload, normalizeEmissao } from '../utils/normalizeEmissao';
import { nanoid } from 'nanoid';
import { moneyFormat } from '~/utils/functions';
import { TituloRow } from '../utils/tabColumns';
import { parseMoney, round2 } from '../utils/parseMoney';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';

const MySwal = withReactContent(Swal);

const confirmTransfer = async (params: {
  qtd: number;
  administradora?: string | null;
  bandeira?: string | null;
}): Promise<boolean> => {
  const result = await MySwal.fire({
    title: 'Confirmar transferência?',
    html: (
      <div style={{ textAlign: 'left' }}>
        <p>
          Você está prestes a transferir <strong>{params.qtd}</strong>{' '}
          título(s).
        </p>
        {params.administradora && (
          <p>
            Administradora: <strong>{params.administradora}</strong>
          </p>
        )}
        {params.bandeira && (
          <p>
            Bandeira: <strong>{params.bandeira}</strong>
          </p>
        )}
        <p>Deseja continuar?</p>
      </div>
    ),
    icon: 'question',
    showCancelButton: true,
    confirmButtonText: 'Sim, transferir',
    cancelButtonText: 'Cancelar',
    focusCancel: true,
    cancelButtonColor: '#DE350B',
  });

  return result.isConfirmed;
};

type TitulosElegiveisProps = {
  cod_conta: number;
  cod_parcela: number;
  cod_loja: number;
  cod_pessoa: number;
  nome_pessoa: string;
  cod_finalizadora: number;
  des_finalizadora: string;
  cod_categoria: number;
  des_categoria: string;
  qtd_parcela: number;
  num_parcela: number;
  val_parcela: number;
  val_juros: number;
  dta_entrada: string;
  dta_vencimento: string;
  num_docto: string;
  tipo_operacao_cartao: number;
};

export type LojaFilter = LojaId | LojaId[] | null;

export interface DateRange {
  ini: Moment;
  fim: Moment;
}

type DisableState = {
  administradora: boolean;
  bandeira: boolean;
  finalizadora_destino: boolean;
  btnSearch: boolean;
  btnCancel: boolean;
  btnClear: boolean;
  inputLoja: boolean;
  inputConta: boolean;
  inputFinalizadora: boolean;
  inputPdv: boolean;
  inputCupomFiscal: boolean;
  inputEmissao: boolean;
  conciliados: boolean;
};

const initialDisable: DisableState = {
  administradora: false,
  bandeira: true,
  finalizadora_destino: false,
  btnSearch: false,
  btnCancel: true,
  btnClear: false,
  inputLoja: false,
  inputConta: false,
  inputFinalizadora: false,
  inputPdv: false,
  inputCupomFiscal: false,
  inputEmissao: false,
  conciliados: false,
};

export interface TransferenciaDePOSContextValue extends UseFormReturn<any> {
  showContent: boolean;
  setShowContent: (v: boolean) => void;

  loja: LojaFilter;
  setLoja: (v: LojaFilter) => void;
  lojaNormalized: LojaId[];

  emissao: DateRange;
  updateEmissao: (partial: Partial<DateRange>) => void;
  isRangeValid: boolean;

  single: number[];
  setSingle: (v: number[]) => void;

  disable: DisableState;
  setDisablePatch: (patch: Partial<DisableState>) => void;

  handleSearch: () => Promise<void>;
  handleTransferir: () => Promise<void>;

  handleCancel: () => void;

  searching: boolean;

  handleClear: () => void;
  setResetLoja: React.Dispatch<React.SetStateAction<boolean>>;
  resetLoja: boolean;
  keyRefDateRange: React.MutableRefObject<any>;
  titulosElegiveis: any[];

  toggleSelection: (row: TituloRow) => void;
  isSelected: (id: string) => boolean;
  selectAll: () => void;
  clearSelection: () => void;
  selectedTotal: number;
  selectedRows: TituloRow[];

  setBandeiraSelecionada: React.Dispatch<any>;
  transferindo: boolean;
  error: {
    cod_pessoa: boolean;
    cod_seq_bandeira: boolean;
  };
  handleError: (pessoa: boolean, bandeira: boolean) => void;
}

const TransferenciaDePOSContext = createContext<
  TransferenciaDePOSContextValue | undefined
>(undefined);

export const TransferenciaDePOSProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const form = useForm<any>({ mode: 'onSubmit' });

  const [showContent, setShowContent] = useState(false);
  const [loja, setLoja] = useState<LojaFilter>(null);
  const [emissao, setEmissao] = useState<DateRange>({
    ini: moment(),
    fim: moment(),
  });
  const [single, setSingle] = useState<number[]>([0]);
  const [disable, setDisable] = useState<DisableState>(initialDisable);
  const [searching, setSearching] = useState(false);
  const [resetLoja, setResetLoja] = useState(false);
  const [titulosElegiveis, setTitulosElegiveis] = useState<any[]>([]);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bandeiraSelecionada, setBandeiraSelecionada] = useState<any>(null);
  const [transferindo, setTransferindo] = useState(false);
  const [error, setError] = useState({
    cod_pessoa: false,
    cod_seq_bandeira: false,
  });

  const keyRefDateRange = useRef<any>(Date.now());

  useEffect(() => {
    const idsAtuais = new Set(titulosElegiveis.map((r) => String(r.id)));
    setSelectedIds((prev) => {
      const next = new Set<string>();
      prev.forEach((id) => {
        if (idsAtuais.has(id)) next.add(id);
      });
      return next;
    });
  }, [titulosElegiveis]);

  const isSelected = useCallback(
    (id: string) => selectedIds.has(String(id)),
    [selectedIds],
  );

  const toggleSelection = useCallback((row: TituloRow) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      const key = String(row.id);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  }, []);

  const selectAll = React.useCallback(() => {
    setSelectedIds(new Set(titulosElegiveis.map((r) => String(r.id))));
  }, [titulosElegiveis]);

  const clearSelection = React.useCallback(() => {
    setSelectedIds(new Set());
  }, []);

  const selectedRows = useMemo<TituloRow[]>(
    () => titulosElegiveis.filter((r) => isSelected(String(r.id))),
    [titulosElegiveis, isSelected],
  );

  const selectedTotal = React.useMemo(
    () =>
      round2(
        selectedRows.reduce((acc, r) => acc + parseMoney(r.val_parcela), 0),
      ),
    [selectedRows],
  );

  const lojaNormalized = useMemo<LojaId[]>(() => {
    if (loja == null) return [];
    const arr = Array.isArray(loja) ? loja : [loja];
    return Array.from(new Set(arr.filter((n) => Number.isFinite(n))));
  }, [loja]);

  const isRangeValid = useMemo<boolean>(() => {
    const { ini, fim } = emissao;
    if (!ini?.isValid() || !fim?.isValid()) return false;
    return ini.isSameOrBefore(fim, 'minute');
  }, [emissao]);

  const emissaoFmt = useMemo<EmissaoPayload>(
    () => normalizeEmissao(emissao),
    [emissao],
  );

  const updateEmissao = useCallback((partial: Partial<DateRange>) => {
    setEmissao((prev) => {
      const next: DateRange = { ...prev, ...partial };
      if (
        next.ini.isValid() &&
        next.fim.isValid() &&
        next.ini.isAfter(next.fim)
      ) {
        return { ini: next.ini, fim: next.ini.clone() };
      }
      return next;
    });
  }, []);

  const setDisablePatch = useCallback((patch: Partial<DisableState>) => {
    setDisable((prev) => {
      let changed = false;
      const next: DisableState = { ...prev };
      (Object.keys(patch) as (keyof DisableState)[]).forEach((k) => {
        const v = patch[k];
        if (typeof v === 'boolean' && v !== prev[k]) {
          next[k] = v;
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, []);

  const handleError = useCallback((pessoa: boolean, bandeira: boolean) => {
    setError({ cod_pessoa: pessoa, cod_seq_bandeira: bandeira });
  }, []);

  const handleSearch = useCallback(async () => {
    if (!isRangeValid) return;

    const validation = validateSearch({
      lojas: lojaNormalized,
      emissao: emissaoFmt,
    });

    if (!validation.ok) {
      validation.errors.forEach((msg) => toast.warning(msg, { toastId: msg }));
      return;
    }

    setSearching(true);
    setResetLoja(false);

    const codFinalizadora = form.getValues('cod_finalizadora')?.value;
    const codCC = form.getValues('cod_cc')?.value;
    const numPdv = form.getValues('num_pdv')?.value;
    const numCupomFiscal = form.getValues('num_cupom_fiscal');

    const payload = {
      lista_lojas_selecionadas: lojaNormalized,
      emissao: emissaoFmt,
      switch_conciliado: single,
      cod_entidade_selecionada: codFinalizadora || undefined,
      cod_cc: codCC || undefined,
      num_pdv: numPdv || undefined,
      num_cupom_fiscal: numCupomFiscal === '' ? undefined : numCupomFiscal,
    };

    const { data } = await api.get('/transferencia-pos/titulos-elegiveis', {
      params: payload,
    });

    if (!data.success) {
      toast.warning(data.message);
      setSearching(false);
      return;
    }

    setShowContent(true);
    setDisablePatch({
      inputLoja: true,
      inputConta: true,
      inputFinalizadora: true,
      inputPdv: true,
      inputCupomFiscal: true,
      inputEmissao: true,
      conciliados: true,
      btnSearch: true,
      btnCancel: false,
      btnClear: true,
    });
    setSearching(false);

    const titulos = data.data as TitulosElegiveisProps[];

    const elegiveis = titulos.map((t) => ({
      id: nanoid(),
      cod_loja: t.cod_loja,
      num_docto: t.num_docto,
      nome_pessoa: t.nome_pessoa,
      des_finalizadora: t.des_finalizadora,
      val_parcela: moneyFormat(t.val_parcela.toFixed(2)),
      val_juros: moneyFormat(t.val_juros.toFixed(2)),
      dta_entrada: moment(t.dta_entrada).format('DD/MM/YYYY'),
      dta_vencimento: moment(t.dta_vencimento).format('DD/MM/YYYY'),
      tipo_operacao_cartao: t.tipo_operacao_cartao,
      cod_conta_receber: t.cod_conta,
      cod_parcela_receber: t.cod_parcela,
    }));

    setTitulosElegiveis(elegiveis);
  }, [emissaoFmt, form, isRangeValid, lojaNormalized, setDisablePatch, single]);

  const handleCancel = useCallback(() => {
    setShowContent(false);
    setSearching(false);
    setResetLoja(false);
    setTitulosElegiveis([]);
    setDisablePatch({
      inputLoja: false,
      inputConta: false,
      inputFinalizadora: false,
      inputPdv: false,
      inputCupomFiscal: false,
      inputEmissao: false,
      conciliados: false,
      btnSearch: false,
      btnCancel: true,
      btnClear: false,
    });
    clearSelection();
  }, [clearSelection, setDisablePatch]);

  const handleClear = useCallback(() => {
    setResetLoja(true);

    setTimeout(() => {
      form.clearErrors();
      form.setValue('cod_cc', { value: undefined, label: undefined });
      form.setValue('cod_finalizadora', { value: undefined, label: undefined });
      form.setValue('num_pdv', { value: undefined, label: undefined });
      form.setValue('num_cupom_fiscal', '');
      form.setValue('cod_pessoa', { value: undefined, label: undefined });
      form.setValue('cod_seq_bandeira', { value: undefined, label: undefined });
      form.setValue('cod_finalizadora_destino', {
        value: undefined,
        label: undefined,
      });
    }, 100);

    setEmissao({ ini: moment(), fim: moment() });
    keyRefDateRange.current = moment();
    setSingle([0]);
  }, [form]);

  const doTransfer = useCallback(
    async (payload: any) => {
      try {
        setTransferindo(true);

        const { data } = await api.put('/transferencia-pos/titulos-elegiveis', {
          payload,
        });

        if (data?.success) {
          toast.success(data.message ?? 'Transferência concluída com sucesso!');
          handleCancel();
          handleClear();
          return;
        }

        toast.error(
          data?.message ?? 'Não foi possível concluir a transferência.',
        );
      } finally {
        setTransferindo(false);
      }
    },
    [handleCancel, handleClear],
  );

  const handleTransferir = useCallback(async () => {
    if (!isRangeValid) {
      toast.warning('Informe um período válido para continuar');
      return;
    }

    if (selectedRows.length === 0) {
      toast.warning('Nenhum título selecionado para transferência!');
      return;
    }

    if (!form.getValues('cod_pessoa')) {
      toast.warning('Necessário informar uma administradora');
      form.setError('cod_pessoa', { type: 'required' });
      setError({ cod_pessoa: true, cod_seq_bandeira: false });

      return;
    }

    if (!form.getValues('cod_seq_bandeira') || !bandeiraSelecionada) {
      toast.warning('Necessário informar uma bandeira');
      form.setError('cod_seq_bandeira', { type: 'required' });
      setError({ cod_pessoa: false, cod_seq_bandeira: true });
      return;
    }

    form.clearErrors(['cod_pessoa', 'cod_seq_bandeira']);
    setError({ cod_pessoa: false, cod_seq_bandeira: false });

    const payload = {
      cod_administradora: form.getValues('cod_pessoa').value,
      cod_bandeira:
        form.getValues('cod_seq_bandeira').value ??
        bandeiraSelecionada.cod_seq_bandeira,
      num_condicao: {
        credito: bandeiraSelecionada.num_condicao_credito,
        debito: bandeiraSelecionada.num_condicao_debito,
      },
      cod_finalizadora:
        form.getValues('cod_finalizadora_destino')?.value ?? undefined,
      titulos: selectedRows,
    };

    const adminLabel: string | null =
      form.getValues('cod_pessoa')?.label ?? null;
    const bandeiraLabel: string | null =
      form.getValues('cod_seq_bandeira')?.label ??
      (bandeiraSelecionada as any)?.descricao ??
      null;

    const confirmed = await confirmTransfer({
      qtd: selectedRows.length,
      administradora: adminLabel,
      bandeira: bandeiraLabel,
    });

    if (!confirmed) return;

    await doTransfer(payload);
  }, [isRangeValid, selectedRows, form, bandeiraSelecionada, doTransfer]);

  const value: TransferenciaDePOSContextValue = useMemo(
    () => ({
      ...form,
      showContent,
      setShowContent,
      loja,
      setLoja,
      lojaNormalized,
      emissao,
      updateEmissao,
      isRangeValid,
      single,
      setSingle,
      disable,
      setDisablePatch,
      handleSearch,
      handleTransferir,
      handleCancel,
      searching,
      handleClear,
      setResetLoja,
      resetLoja,
      keyRefDateRange,
      titulosElegiveis,
      toggleSelection,
      isSelected,
      selectAll,
      clearSelection,
      selectedTotal,
      selectedRows,
      setBandeiraSelecionada,
      transferindo,
      error,
      handleError,
    }),
    [
      form,
      showContent,
      loja,
      lojaNormalized,
      emissao,
      isRangeValid,
      single,
      updateEmissao,
      handleSearch,
      handleTransferir,
      disable,
      setDisablePatch,
      handleCancel,
      searching,
      handleClear,
      resetLoja,
      titulosElegiveis,
      toggleSelection,
      isSelected,
      selectAll,
      clearSelection,
      selectedTotal,
      selectedRows,
      transferindo,
      error,
      handleError,
    ],
  );

  return (
    <TransferenciaDePOSContext.Provider value={value}>
      {children}
    </TransferenciaDePOSContext.Provider>
  );
};

export const useTransferenciaDePOS = (): TransferenciaDePOSContextValue => {
  const ctx = useContext(TransferenciaDePOSContext);
  if (!ctx)
    throw new Error('useTransferenciaDePOS deve ser usado dentro do Provider');
  return ctx;
};
